import { type FC, useEffect } from "react";
import { useDoc, usePouch, useView } from "use-pouchdb";

import { assertIsIconDoc, assertIsItemInfo, type ItemInfo } from "../common/schema";
import { getImageClass } from "./image_attachments";
import { assertIsExistingIconDoc, assertIsExistingItemAttachmentDoc, type ExistingIconDoc } from "./existing_docs";
import { backfillThumbnail } from "./thumbnails";

const iconImageDivStyle = (style?: React.CSSProperties): React.CSSProperties => ({
  ...style,
  backgroundImage: "var(--image)",
  backgroundSize: "contain",
  backgroundRepeat: "no-repeat",
  backgroundPosition: "center",
});

export const IconImage: FC<{iconId: string; style?: React.CSSProperties}> = ({iconId, style}) => {
  const db = usePouch();
  const { doc } = useDoc(iconId);
  if (doc == null) return <div style={style} />;
  assertIsExistingIconDoc(doc);
  const attachName = doc._attachments?.["thumbnail"] != null ? "thumbnail" : "file";
  const imageClass = getImageClass(db, doc, attachName);
  return <div className={imageClass ?? ""} style={iconImageDivStyle(style)} />;
};

export const IconImageFromDoc: FC<{doc: ExistingIconDoc; style?: React.CSSProperties}> = ({doc, style}) => {
  const db = usePouch();

  useEffect(() => {
    backfillThumbnail(db, doc).catch(console.error);
  }, [db, doc._id, doc._rev]);

  const attachName = doc._attachments?.["thumbnail"] != null ? "thumbnail" : "file";
  const imageClass = getImageClass(db, doc, attachName);
  return <div className={imageClass ?? ""} style={iconImageDivStyle(style)} />;
};

export const IconNameDisplay: FC<{iconId: string}> = ({iconId}) => {
  const { doc } = useDoc(iconId);
  if (doc == null) return null;
  assertIsIconDoc(doc);
  return <span>{doc.name}</span>;
};

const PrimaryAttachmentImage: FC<{itemId: string; style?: React.CSSProperties}> = ({itemId, style}) => {
  const db = usePouch();
  const { rows } = useView("built/primary_image_by_item", { key: itemId, include_docs: true });
  const row = rows[0];

  useEffect(() => {
    if (row?.doc == null) return;
    assertIsExistingItemAttachmentDoc(row.doc);
    backfillThumbnail(db, row.doc).catch(console.error);
  }, [db, row?.doc?._id, row?.doc?._rev]);

  if (row?.doc == null) return <div style={style} />;
  assertIsExistingItemAttachmentDoc(row.doc);
  const attachName = row.doc._attachments?.["thumbnail"] != null ? "thumbnail" : "file";
  const imageClass = getImageClass(db, row.doc, attachName);
  return <div className={imageClass ?? ""} style={iconImageDivStyle(style)} />;
};

const TemplateImageFallback: FC<{templateId: string; style?: React.CSSProperties}> = ({templateId, style}) => {
  const { doc } = useDoc<Partial<ItemInfo>>(templateId);
  const styleProps = style !== undefined ? {style} : {};
  if (doc == null) return <div style={style} />;
  assertIsItemInfo(doc);
  if (doc.icon_id != null) return <IconImage iconId={doc.icon_id} {...styleProps} />;
  return <PrimaryAttachmentImage itemId={templateId} {...styleProps} />;
};

const PrimaryAttachmentImageWithFallback: FC<{itemId: string; templateId?: string | null; style?: React.CSSProperties}> = ({itemId, templateId, style}) => {
  const db = usePouch();
  const { rows } = useView("built/primary_image_by_item", { key: itemId, include_docs: true });
  const styleProps = style !== undefined ? {style} : {};
  const row = rows[0];

  useEffect(() => {
    if (row?.doc == null) return;
    assertIsExistingItemAttachmentDoc(row.doc);
    backfillThumbnail(db, row.doc).catch(console.error);
  }, [db, row?.doc?._id, row?.doc?._rev]);

  if (row?.doc != null) {
    assertIsExistingItemAttachmentDoc(row.doc);
    const attachName = row.doc._attachments?.["thumbnail"] != null ? "thumbnail" : "file";
    const imageClass = getImageClass(db, row.doc, attachName);
    return <div className={imageClass ?? ""} style={iconImageDivStyle(style)} />;
  }
  if (templateId != null) return <TemplateImageFallback templateId={templateId} {...styleProps} />;
  return <div style={style} />;
};

export const PrimaryImageThumbnail: FC<{itemId: string; iconId?: string | null; templateId?: string | null; style?: React.CSSProperties}> = ({itemId, iconId, templateId, style}) => {
  const styleProps = style !== undefined ? {style} : {};
  const templateProps = templateId != null ? {templateId} : {};
  if (iconId != null) return <IconImage iconId={iconId} {...styleProps} />;
  return <PrimaryAttachmentImageWithFallback itemId={itemId} {...templateProps} {...styleProps} />;
};
